import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Shield, AlertTriangle, Upload, Camera, Phone } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface VerificationCenterProps {
  userId: string;
}

export const VerificationCenter = ({ userId }: VerificationCenterProps) => {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: verification, isLoading } = useQuery({
    queryKey: ['user-verification', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_verifications')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      return data;
    }
  });

  const submitVerificationMutation = useMutation({
    mutationFn: async (method: string) => {
      const { error } = await supabase
        .from('user_verifications')
        .upsert({
          user_id: userId,
          status: 'pending',
          verification_method: method,
          verification_data: { method, submittedAt: new Date().toISOString() }
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Verification submitted!",
        description: "Your verification request has been submitted for review."
      });
      queryClient.invalidateQueries({ queryKey: ['user-verification'] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to submit verification request.",
        variant: "destructive"
      });
    }
  });

  const verificationMethods = [
    {
      id: 'photo_id',
      title: 'Photo ID Verification',
      description: 'Upload a government-issued photo ID',
      icon: Upload,
      difficulty: 'Easy',
      timeEstimate: '2-3 minutes'
    },
    {
      id: 'selfie_video',
      title: 'Selfie Video',
      description: 'Record a short video saying your name',
      icon: Camera,
      difficulty: 'Easy',
      timeEstimate: '1-2 minutes'
    },
    {
      id: 'phone_number',
      title: 'Phone Verification',
      description: 'Verify your phone number with SMS',
      icon: Phone,
      difficulty: 'Very Easy',
      timeEstimate: '30 seconds'
    }
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-48 mb-2" />
            <div className="h-4 bg-muted rounded w-64" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  const getStatusConfig = () => {
    switch (verification?.status) {
      case 'verified':
        return {
          title: 'Verified Human ✨',
          description: 'You are verified as a real human on Positivio!',
          color: 'text-green-600',
          bgColor: 'bg-green-50 border-green-200',
          icon: CheckCircle
        };
      case 'pending':
        return {
          title: 'Verification Pending',
          description: 'Your verification is being reviewed. This usually takes 24-48 hours.',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 border-yellow-200',
          icon: AlertTriangle
        };
      case 'rejected':
        return {
          title: 'Verification Failed',
          description: 'Your verification was not approved. Please try again with a different method.',
          color: 'text-red-600',
          bgColor: 'bg-red-50 border-red-200',
          icon: AlertTriangle
        };
      default:
        return {
          title: 'Get Verified as a Real Human',
          description: 'Join the verified community and prove you\'re a real person, not a bot!',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 border-blue-200',
          icon: Shield
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6">
      <Card className={`${statusConfig.bgColor} border-2`}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <StatusIcon className={`w-8 h-8 ${statusConfig.color}`} />
            <div>
              <CardTitle className={statusConfig.color}>{statusConfig.title}</CardTitle>
              <CardDescription className="mt-1">
                {statusConfig.description}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {verification?.status === 'verified' && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Welcome to the Verified Community!</h3>
              <p className="text-muted-foreground mb-4">
                You're now part of an exclusive network of verified humans. Enjoy authentic connections without bots!
              </p>
              <Badge className="bg-green-500 hover:bg-green-600">
                <CheckCircle className="w-4 h-4 mr-2" />
                Verified Human
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {(!verification || verification.status === 'rejected') && (
        <div className="grid gap-4">
          <h3 className="text-lg font-semibold">Choose Your Verification Method</h3>
          {verificationMethods.map((method) => {
            const Icon = method.icon;
            return (
              <Card 
                key={method.id} 
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedMethod === method.id ? 'ring-2 ring-primary border-primary' : ''
                }`}
                onClick={() => setSelectedMethod(method.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Icon className="w-6 h-6 text-primary mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{method.title}</h4>
                        <Badge variant="outline" className="text-xs">
                          {method.difficulty}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {method.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Estimated time: {method.timeEstimate}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {selectedMethod && (
            <Button 
              onClick={() => submitVerificationMutation.mutate(selectedMethod)}
              disabled={submitVerificationMutation.isPending}
              className="w-full"
            >
              {submitVerificationMutation.isPending ? 'Submitting...' : 'Start Verification'}
            </Button>
          )}
        </div>
      )}

      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Why Get Verified?</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Join an exclusive bot-free community</li>
                <li>• Build trust with verified badge</li>
                <li>• Access to verified-only features</li>
                <li>• Higher priority in friend suggestions</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};